/**
 * Which destination sits in the fourth bottom-bar slot.
 *
 * That slot has changed hands twice already (Search, then Raid, then Search
 * again), each time as a code edit and an argument about whose need is bigger.
 * It is now the trainer's choice, which ends the argument: the bar carries what
 * that trainer actually uses.
 *
 * BUILT AS A REGISTRY, not a boolean. Adding My Pokemon or Raid later is one
 * entry here plus a Settings option that renders itself from this list, with no
 * further edits to the bar, the tour or the Settings screen. That is the whole
 * reason for the shape.
 *
 * The default is META. Search moved out of the bar rather than off the map: it
 * keeps its main-menu row, and existing trainers get a one-time notice saying
 * where it went and how to put it back (see SearchMovedNotice).
 */
import { ArrowLeftRight, Gift, Heart, Scale, Search, Swords, Tag, Trash2 } from "lucide-react";
import type { ComponentType } from "react";

export interface BottomSlotOption {
  key: string;
  /** Bar label. Short: it sits under an icon in a quarter of the screen. */
  label: string;
  /** Settings-row description. */
  hint: string;
  /**
   * Where the tab navigates. Empty for picker-backed options (Compare, Trade)
   * — those destinations are friend-scoped routes, so the tab opens the same
   * FriendPickerDialog the main menu uses instead of navigating directly.
   */
  href: string;
  /** Set on friend-scoped options: tapping the tab opens the friend picker. */
  picker?: "compare" | "trade";
  /**
   * Active-state matcher for picker options, whose landing route embeds a
   * friend id (`/friends/:id/compare`) and so cannot be prefix-matched off
   * `href` (that prefix belongs to the Friends tab).
   */
  activePattern?: RegExp;
  icon: ComponentType<{ className?: string; strokeWidth?: number | string }>;
  /** The bar's data-testid, which the tour targets by name. */
  testid: string;
}

export const BOTTOM_SLOT_OPTIONS: readonly BottomSlotOption[] = [
  {
    // My Pokémon shipped 2026-08-20 and took the default slot (user
    // direction): the trainer's own Pokémon — IVs, moves, PvP standing — is
    // the surface a companion app opens for. "Pokémon" because "My Pokémon"
    // wraps under a bar icon. Meta and Search stay one Settings tap away.
    key: "myPokemon",
    label: "Pokémon",
    hint: "Your own Pokémon: IVs, moves, and what to do with each",
    href: "/my-pokemon",
    icon: Heart,
    testid: "tab-my-pokemon",
  },
  {
    key: "meta",
    label: "Meta",
    hint: "PvP and raid rankings, and how your dex measures up",
    href: "/battle",
    icon: Swords,
    testid: "tab-meta",
  },
  {
    key: "search",
    label: "Search",
    hint: "Build a Pokémon GO search string",
    href: "/trade",
    icon: Search,
    testid: "tab-search",
  },
  // 2026-08-27 (user ask): Compare and Trade join the slot. Both live at
  // friend-scoped routes, so the tab opens the friend picker (same dialog +
  // copy as the main-menu rows) rather than navigating to a bare path.
  {
    key: "compare",
    label: "Compare",
    hint: "Head to head with one friend",
    href: "",
    picker: "compare",
    activePattern: /^\/friends\/[^/]+\/compare/,
    icon: Scale,
    testid: "tab-compare",
  },
  {
    key: "trade",
    label: "Trade",
    hint: "What the two of you could swap",
    href: "",
    picker: "trade",
    activePattern: /^\/friends\/[^/]+\/trades/,
    icon: ArrowLeftRight,
    testid: "tab-trade",
  },
  // 2026-08-31 (user ask): the three pages a trainer opens mid-session, in
  // the bar for whoever lives in one of them. Plain routes, so the registry
  // carries them with no further edits to the bar, the tour or Settings.
  {
    key: "bonuses",
    label: "Bonuses",
    hint: "What's boosted right now, and what unlocks next",
    href: "/bonuses",
    icon: Gift,
    testid: "tab-bonuses",
  },
  {
    key: "naming",
    label: "Naming",
    hint: "Put the IVs in the name, ready to paste into GO",
    href: "/naming",
    icon: Tag,
    testid: "tab-naming",
  },
  {
    key: "cleanup",
    label: "Cleanup",
    hint: "Free up storage without losing a keeper",
    href: "/cleanup",
    icon: Trash2,
    testid: "tab-cleanup",
  },
];

export const DEFAULT_BOTTOM_SLOT = "myPokemon";

const STORAGE_KEY = "nexus.bottomSlot.v1";

/** Fired when the choice changes, so the bar re-renders without a reload. */
export const BOTTOM_SLOT_EVENT = "nexus:bottom-slot-changed";

export function bottomSlotOption(key: string): BottomSlotOption {
  return (
    BOTTOM_SLOT_OPTIONS.find((o) => o.key === key) ??
    BOTTOM_SLOT_OPTIONS.find((o) => o.key === DEFAULT_BOTTOM_SLOT)!
  );
}

/**
 * The chosen slot. Falls back to the default for anything unrecognised, so a
 * stored key from a removed option cannot empty the bar.
 */
/**
 * One-time 1.8.0 migration marker. Devices that carried a stored "meta"
 * BEFORE My Pokémon existed never CHOSE meta over it — the option wasn't
 * there to refuse — so the launch moves them onto the new default exactly
 * once. A trainer who then picks Meta back in Settings keeps it forever
 * (the marker blocks any re-migration).
 */
const MIGRATED_KEY = "nexus.bottomSlot.migrated180";

export function readBottomSlot(): string {
  if (typeof window === "undefined") return DEFAULT_BOTTOM_SLOT;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && !window.localStorage.getItem(MIGRATED_KEY)) {
      window.localStorage.setItem(MIGRATED_KEY, "1");
      if (raw === "meta") {
        window.localStorage.setItem(STORAGE_KEY, DEFAULT_BOTTOM_SLOT);
        return DEFAULT_BOTTOM_SLOT;
      }
    }
    if (!raw) return DEFAULT_BOTTOM_SLOT;
    return BOTTOM_SLOT_OPTIONS.some((o) => o.key === raw) ? raw : DEFAULT_BOTTOM_SLOT;
  } catch {
    return DEFAULT_BOTTOM_SLOT;
  }
}

export function setBottomSlot(key: string): void {
  if (typeof window === "undefined") return;
  const valid = BOTTOM_SLOT_OPTIONS.some((o) => o.key === key) ? key : DEFAULT_BOTTOM_SLOT;
  try {
    window.localStorage.setItem(STORAGE_KEY, valid);
  } catch {
    /* the bar falls back to the default; nothing else breaks. */
  }
  window.dispatchEvent(new CustomEvent(BOTTOM_SLOT_EVENT));
}

/** Test-only: forget the choice. */
export function __resetBottomSlotForTests(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(MIGRATED_KEY);
  } catch {
    /* nothing to do. */
  }
}
